import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Typography
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Close, SaveAlt } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext } from "formik";
import Drawer from "../../../../components/Drawer/Drawer";
import { homeworkFeedbackResponseTypes } from "../../../../constants";
import Select from "../../../../components/FormikFields/Select";
import HTMLEditor from "../../../../components/HtmlEditor/HtmlEditor";
import TextFieldUploadButton from "../../../../components/Buttons/TextFieldUploadButton";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import LinkButton from "../../../../components/Buttons/LinkButton";

const useStyles = makeStyles(theme => ({
  form: {
    flex: 1,
    display: "flex"
  }
}));

const FeedbackFormFileButton = ({
  onFileChange,
  file,
  disabled,
  isPublished
}) => {
  return !isPublished && !file ? (
    <TextFieldUploadButton
      disableUnderline={false}
      onFileUpload={onFileChange}
      file={file}
      disabled={disabled}
    />
  ) : (
    <Box ml={-1} display="flex">
      <LinkButton
        href={file.url}
        text={file.fileName}
        download={true}
        endAdornment={isPublished ? <SaveAlt /> : null}
      />
      {!isPublished && (
        <IconButton
          color="primary"
          onClick={() => onFileChange(null)}
          disabled={disabled}
          size="large"
        >
          <Close />
        </IconButton>
      )}
    </Box>
  );
};

const FeedbackForm = ({ comment, isPublished, onSubmit, isSaving }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const [currentFile, setCurrentFile] = useState(null);

  const feedbackResponseOptions = [
    {
      value: homeworkFeedbackResponseTypes.writtenResponse,
      label: t("homeworkFeedbackEditor.writeResponse")
    },

    {
      value: homeworkFeedbackResponseTypes.uploadedResponse,
      label: t("homeworkFeedbackEditor.uploadResponse")
    }
  ];

  useEffect(() => {
    // Set the current file to the file associated with the previously saved/published file if it exists
    // and if the user has not uploaded a new file
    if (comment && comment.filePath && !values.file) {
      setCurrentFile({ fileName: comment.filename, url: comment.filePath });
    }
  }, [comment]);

  useEffect(() => {
    if (values.file) {
      setCurrentFile({
        fileName: values.file.name,
        url: URL.createObjectURL(values.file)
      });
    }
  }, [values.file]);

  const handleFileChange = newFile => {
    setFieldValue("file", newFile);
    if (!newFile) {
      setCurrentFile(null);
    }
  };

  const buttonsDisabled =
    isPublished ||
    isSaving ||
    (values.responseType === homeworkFeedbackResponseTypes.writtenResponse &&
      !values.content) ||
    (values.responseType === homeworkFeedbackResponseTypes.uploadedResponse &&
      !currentFile);

  return (
    <Form className={classes.form}>
      <Box display="flex" flexDirection="column" mt={4} flex={1}>
        <Select
          name="responseType"
          menuItems={feedbackResponseOptions}
          disabled={isPublished || isSaving}
        />
        <Box mt={4} mb={2} flex={1}>
          {values.responseType ===
            homeworkFeedbackResponseTypes.writtenResponse && (
            <HTMLEditor
              disabled={isSaving}
              height="100%"
              disableResize
              disableUnderline
              disableMargin
              onChange={value => {
                setFieldValue("content", value);
              }}
              readOnly={isPublished}
              value={values.content}
              onFileAdd={(fileObj, tempLink, type) =>
                setFieldValue("contentFileList", [
                  ...values.contentFileList,
                  { fileObj, tempLink, type }
                ])
              }
              menubar={false}
              toolbar="link image media code | styleselect bold italic underline | outdent indent bullist | fontselect fontsizeselect forecolor"
            />
          )}
          {values.responseType ===
            homeworkFeedbackResponseTypes.uploadedResponse && (
            <FeedbackFormFileButton
              onFileChange={handleFileChange}
              file={currentFile}
              isPublished={isPublished}
              disabled={isSaving}
            />
          )}
        </Box>

        <ButtonGroup alignItems="center">
          {isSaving && <CircularProgress color="primary" size={24} />}
          <Button
            color="primary"
            disabled={buttonsDisabled}
            onClick={() => onSubmit(false)}
          >
            {t("homeworkFeedbackEditor.saveBtn")}
          </Button>
          {/**   <Button
            variant="contained"
            color="primary"
            disabled={buttonsDisabled}
            onClick={() => onSubmit(true)}
          >
            {t("homeworkFeedbackEditor.publishFeedbackBtn")}
      </Button>  */}
        </ButtonGroup>
      </Box>
    </Form>
  );
};

const HomeworkFeedbackPanel = ({
  comment,
  isPublished,
  expanded,
  onClose,
  onSaveFeedback,
  isSaving
}) => {
  const { t } = useTranslation();
  return (
    <Drawer expanded={expanded} onClose={onClose}>
      <Box
        p={4}
        height="100%"
        display="flex"
        flexDirection="column"
        data-qa="homework-feedback-panel"
      >
        <Typography variant="h5" noWrap sx={{ pb: 2 }}>
          {t("homeworkFeedbackEditor.provideFeedback")}
        </Typography>
        <Divider />
        <Formik
          initialValues={{
            content: comment ? comment.content : "",
            file: null,
            responseType:
              comment && comment.filePath
                ? homeworkFeedbackResponseTypes.uploadedResponse
                : homeworkFeedbackResponseTypes.writtenResponse,
            contentFileList: []
          }}
          enableReinitialize
        >
          {({ values }) => (
            <FeedbackForm
              comment={comment}
              isPublished={isPublished}
              onSubmit={onSaveFeedback(values)}
              isSaving={isSaving}
            />
          )}
        </Formik>
      </Box>
    </Drawer>
  );
};

export default HomeworkFeedbackPanel;

HomeworkFeedbackPanel.propTypes = {
  isPublished: PropTypes.bool
};
